import {
    Button,
    Menu,
    MenuDivider,
    MenuItem,
    Popover
} from "@blueprintjs/core";
import { IconName, IconNames } from "@blueprintjs/icons";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { segmentTrackOpenBulkScenarioUpload } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useIsBulkScenarioUploadEnabled } from "in_queue/hooks/useIsBulkScenarioUploadEnabled";
import { useIsScenarioCreationEnabled } from "in_queue/hooks/useIsScenarioCreationEnabled";
import { ScenarioStage } from "in_queue/types/scenarioType";
import {
    RERUN_ICON,
    RERUN_VERB,
    ScenarioSelectionMode
} from "in_queue/utils/scenarioSelection";
import { useNavigate, useParams } from "react-router-dom";
import { ADMIN_TYPE } from "types/roleType";
import { BulkScenarioUploadLabel } from "../BulkScenarioUpload/bulkScenarioUploadLabels";
import { AdminOnlyTooltip } from "../common/AdminOnlyTooltip";
import { AllScenarioTooltips } from "../common/AllScenarioTooltips";
import { useScenarioBuilderRoutes } from "../ScenarioBuilder/useScenarioBuilderRoutes";
import css from "./ScenarioActionsMenu.module.scss";

const MENU_ITEM_INFO: Record<
    ScenarioSelectionMode,
    { label: string; icon: IconName }
> = {
    [ScenarioSelectionMode.Compare]: {
        label: "Compare scenarios",
        icon: IconNames.COMPARISON
    },
    [ScenarioSelectionMode.Rerun]: {
        label: `${RERUN_VERB} scenarios`,
        icon: RERUN_ICON
    },
    [ScenarioSelectionMode.Archive]: {
        label: "Archive scenarios",
        icon: IconNames.ARCHIVE
    }
};

const ScenarioActionMenuItem: React.FC<{
    mode: ScenarioSelectionMode;
    disabled: boolean;
    onSelect: (mode: ScenarioSelectionMode) => void;
}> = ({ mode, disabled, onSelect }) => {
    const { label, icon } = MENU_ITEM_INFO[mode];
    return (
        <MenuItem
            icon={icon}
            text={label}
            disabled={disabled}
            onClick={() => onSelect(mode)}
        />
    );
};

export const ScenarioActionsMenu: React.FC<{
    scenarioStage: ScenarioStage;
    selectionMode: ScenarioSelectionMode | undefined;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onSelectMode: (mode: ScenarioSelectionMode) => void;
}> = ({ scenarioStage, selectionMode, isOpen, setIsOpen, onSelectMode }) => {
    const { role } = useUserDataContext();
    const navigate = useNavigate();
    const cluster = useCluster();
    const { projectId } = useParams();
    const scenarioBuilderRoutes = useScenarioBuilderRoutes();
    const isBulkUploadEnabled = useIsBulkScenarioUploadEnabled();
    const scenarioCreationEnabled = useIsScenarioCreationEnabled({
        stage: scenarioStage
    });

    const openBulkUpload = () => {
        if (projectId !== undefined) {
            segmentTrackOpenBulkScenarioUpload({
                cluster,
                projectId,
                scenarioStage
            });
        }
        navigate(scenarioBuilderRoutes.bulkUploadHref(scenarioStage));
    };

    return (
        <Popover
            isOpen={isOpen}
            onInteraction={setIsOpen}
            placement="bottom-end"
            content={
                <Menu className={css["ScenarioActionsMenu-menu"]}>
                    <ScenarioActionMenuItem
                        mode={ScenarioSelectionMode.Compare}
                        disabled={false}
                        onSelect={onSelectMode}
                    />
                    <AllScenarioTooltips stage={scenarioStage}>
                        <ScenarioActionMenuItem
                            mode={ScenarioSelectionMode.Rerun}
                            disabled={!scenarioCreationEnabled}
                            onSelect={onSelectMode}
                        />
                    </AllScenarioTooltips>
                    <AdminOnlyTooltip>
                        <ScenarioActionMenuItem
                            mode={ScenarioSelectionMode.Archive}
                            disabled={role !== ADMIN_TYPE}
                            onSelect={onSelectMode}
                        />
                    </AdminOnlyTooltip>
                    {isBulkUploadEnabled && (
                        <>
                            <MenuDivider />
                            <AllScenarioTooltips stage={scenarioStage}>
                                <MenuItem
                                    icon={IconNames.UPLOAD}
                                    text={BulkScenarioUploadLabel.MenuItem}
                                    disabled={!scenarioCreationEnabled}
                                    onClick={openBulkUpload}
                                />
                            </AllScenarioTooltips>
                        </>
                    )}
                </Menu>
            }
        >
            <Button
                text="Scenario actions"
                endIcon={IconNames.CARET_DOWN}
                active={selectionMode !== undefined}
            />
        </Popover>
    );
};
